/**
 * Typed fetch client over `@mychrono/contracts`.
 *
 * Responsibilities, in order of importance:
 *  1. Every method is typed against the wire contract — no `any` reaches a page.
 *  2. The access token is attached automatically.
 *  3. A 401 triggers exactly one refresh attempt, shared across concurrent
 *     requests, and the original request is retried once. If the refresh fails
 *     the session is dropped and the browser goes to /login carrying the
 *     destination in `?next=`.
 *  4. Failures throw `ApiError`, which carries the HTTP status plus the
 *     server's machine-readable `error`/`field`/`requestId`.
 */

import type {
  AdminStats,
  AdminUserView,
  ApiError as ApiErrorBody,
  AuditEvent,
  AuditQuery,
  AuthProviders,
  AuthResponse,
  AuthTokens,
  CompareRequest,
  CompareResponse,
  CreateSessionRequest,
  DeviceListFilesResponse,
  DiscoveredDevice,
  GoogleAuthResponse,
  LeaderboardResponse,
  LapSummaryDto,
  LapTraceDto,
  LoginEvent,
  LoginRequest,
  NetworkComparisonResponse,
  NetworkComparisonResponse as NetworkComparison,
  PackedFloatArray,
  Paginated,
  RegisterRequest,
  SessionDetailDto,
  SessionSummaryDto,
  SessionVisibility,
  TrackDto,
  UpdateUserRequest,
  User,
  UserRole,
  UserStats,
  UserStatus,
} from '@mychrono/contracts';

/**
 * Onde fica a API.
 *
 * Na web é um caminho relativo, porque o mesmo processo serve o cliente e a API.
 * No app nativo não existe "mesmo processo": o pacote é servido de dentro do
 * aparelho, e `/api` cairia no próprio bundle. Por isso o endereço absoluto
 * quando o Capacitor está presente.
 */
export const NATIVE_API_ORIGIN =
  (import.meta.env?.VITE_API_ORIGIN as string | undefined) ?? 'https://mychrono.murilinxd.vps-kinghost.net';

export function ehNativo(): boolean {
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return Boolean(cap?.isNativePlatform?.());
}

export const API_BASE = ehNativo() ? `${NATIVE_API_ORIGIN}/api` : '/api';

/**
 * Where the "Continuar com Google" button points.
 *
 * Deliberately a URL for a full-page navigation rather than a fetch: the browser
 * has to leave for accounts.google.com and come back, and a redirect chain is not
 * something XHR can follow across origins. `next` rides along so the round trip
 * can land on the page the driver was trying to reach.
 */
export function googleStartUrl(next?: string): string {
  const target = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
  const params = new URLSearchParams();
  if (target !== '/') params.set('next', target);
  // No app nativo o link abre no navegador (host externo), e `app=1` faz o
  // servidor devolver a pessoa ao app no fim, em vez de ao site.
  if (ehNativo()) params.set('app', '1');
  const query = params.toString() ? `?${params.toString()}` : '';
  return `${API_BASE}/auth/google/start${query}`;
}

/**
 * One device currently signed in as the user — a live refresh token.
 * Structurally the same rows the admin panel shows under `AdminUserView`.
 */
export type ActiveAuthSession = AdminUserView['activeSessions'][number];

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown by every method on `api`. `status` 0 means the request never reached
 * the server (offline, DNS, CORS) — worth a different message in the UI.
 */
export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable code from the server, e.g. `invalid_credentials`. */
  readonly code: string;
  /** Set on validation failures: which form field to focus. */
  readonly field?: string;
  readonly requestId?: string;
  readonly body?: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    extra: { field?: string; requestId?: string; body?: unknown } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.field = extra.field;
    this.requestId = extra.requestId;
    this.body = extra.body;
  }

  /** True when retrying could plausibly succeed. */
  get isRetriable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }

  get isOffline(): boolean {
    return this.status === 0;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Message to show the user for any thrown value, already in pt-BR. */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.isOffline) return 'Sem conexão com o servidor. Verifique sua internet e tente de novo.';
    return error.message;
  }
  if (error instanceof Error && error.name === 'AbortError') return 'Requisição cancelada.';
  if (error instanceof Error) return error.message;
  return 'Erro inesperado.';
}

// ---------------------------------------------------------------------------
// Auth bridge
//
// api.ts must not import the auth store (the store imports this module), so the
// store registers itself here at module init instead.
// ---------------------------------------------------------------------------

export interface ApiAuthBridge {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  /** Called after a successful silent refresh so the store can persist them. */
  onTokensRefreshed: (tokens: AuthTokens) => void;
  /** Called when the session is unrecoverable; the store should clear itself. */
  onAuthLost: () => void;
}

let bridge: ApiAuthBridge | null = null;

export function configureApiAuth(next: ApiAuthBridge): void {
  bridge = next;
}

let redirecting = false;

function redirectToLogin(): void {
  if (redirecting || typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  if (pathname === '/login' || pathname === '/registrar') return;
  redirecting = true;
  const next = encodeURIComponent(`${pathname}${search}`);
  window.location.assign(`/login?next=${next}`);
}

// ---------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------

export type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  /** Skip the Authorization header (auth endpoints that mint tokens). */
  anonymous?: boolean;
  /** Skip the 401 refresh-and-retry dance (used by the refresh call itself). */
  noRetry?: boolean;
}

/**
 * Narrows a typed query interface to the flat shape `buildUrl` accepts.
 * TypeScript will not implicitly assign an interface to an index-signature type,
 * so this walks the entries instead of asserting.
 */
function toQuery(source: object): Query {
  const out: Query = {};
  for (const [key, value] of Object.entries(source)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value as QueryValue;
  }
  return out;
}

function buildUrl(path: string, query?: Query): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: unknown;
  let message = `Falha na requisição (${response.status}).`;
  let code = `http_${response.status}`;
  let field: string | undefined;
  let requestId: string | undefined;

  try {
    const text = await response.text();
    if (text) {
      body = JSON.parse(text) as unknown;
      const parsed = body as Partial<ApiErrorBody>;
      if (typeof parsed.message === 'string' && parsed.message) message = parsed.message;
      if (typeof parsed.error === 'string' && parsed.error) code = parsed.error;
      if (typeof parsed.field === 'string') field = parsed.field;
      if (typeof parsed.requestId === 'string') requestId = parsed.requestId;
    }
  } catch {
    /* Non-JSON error body: keep the generic message. */
  }

  if (!requestId) requestId = response.headers.get('x-request-id') ?? undefined;
  return new ApiError(response.status, code, message, { field, requestId, body });
}

let refreshInFlight: Promise<AuthTokens | null> | null = null;

async function runRefresh(): Promise<AuthTokens | null> {
  const refreshToken = bridge?.getRefreshToken() ?? null;
  if (!refreshToken) return null;
  try {
    const response = await fetch(buildUrl('/auth/refresh'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      credentials: 'same-origin',
    });
    if (!response.ok) return null;
    const tokens = (await response.json()) as AuthTokens;
    if (!tokens?.accessToken) return null;
    bridge?.onTokensRefreshed(tokens);
    return tokens;
  } catch {
    return null;
  }
}

/** One refresh at a time, shared by every request that hit a 401 together. */
function refreshTokens(): Promise<AuthTokens | null> {
  if (!refreshInFlight) {
    refreshInFlight = runRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function rawRequest(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

  if (options.body !== undefined && !isFormData) headers['content-type'] = 'application/json';
  if (!options.anonymous) {
    const token = bridge?.getAccessToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  return fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body:
      options.body === undefined
        ? undefined
        : isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
    credentials: 'same-origin',
  });
}

async function send(path: string, options: RequestOptions = {}): Promise<Response> {
  let response: Response;
  try {
    response = await rawRequest(path, options);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'network_error', 'Sem conexão com o servidor.', { body: error });
  }

  if (response.status !== 401 || options.anonymous || options.noRetry) {
    if (!response.ok) throw await parseError(response);
    return response;
  }

  const tokens = await refreshTokens();
  if (!tokens) {
    bridge?.onAuthLost();
    redirectToLogin();
    throw new ApiError(401, 'session_expired', 'Sua sessão expirou. Entre novamente.');
  }

  try {
    response = await rawRequest(path, options);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'network_error', 'Sem conexão com o servidor.', { body: error });
  }

  if (response.status === 401) {
    bridge?.onAuthLost();
    redirectToLogin();
    throw new ApiError(401, 'session_expired', 'Sua sessão expirou. Entre novamente.');
  }
  if (!response.ok) throw await parseError(response);
  return response;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const response = await send(path, options);
  return response.blob();
}

// ---------------------------------------------------------------------------
// PackedFloatArray codec
// ---------------------------------------------------------------------------

/** Decodes a wire-transported Float32Array. */
export function unpackFloats(packed: PackedFloatArray | null | undefined): Float32Array {
  if (!packed || !packed.data) return new Float32Array(0);
  const binary = atob(packed.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  // Copy into a fresh buffer: the Float32Array view requires 4-byte alignment.
  return new Float32Array(bytes.buffer, 0, Math.floor(bytes.byteLength / 4)).slice(
    0,
    packed.length || Math.floor(bytes.byteLength / 4),
  );
}

/** Encodes a Float32Array for transport. */
export function packFloats(values: Float32Array): PackedFloatArray {
  const bytes = new Uint8Array(values.buffer, values.byteOffset, values.byteLength);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return { length: values.length, data: btoa(binary) };
}

/** Decodes every channel of a lap trace in one call. */
export function unpackTrace(trace: LapTraceDto): {
  t: Float32Array;
  speed: Float32Array;
  inlineAcc: Float32Array;
  latAcc: Float32Array;
  rpm: Float32Array;
  steer: Float32Array;
  x: Float32Array;
  y: Float32Array;
  offset: Float32Array;
  sectors: Float32Array;
} {
  return {
    t: unpackFloats(trace.channels.t),
    speed: unpackFloats(trace.channels.speed),
    inlineAcc: unpackFloats(trace.channels.inlineAcc),
    latAcc: unpackFloats(trace.channels.latAcc),
    rpm: unpackFloats(trace.channels.rpm),
    steer: unpackFloats(trace.channels.steer),
    x: unpackFloats(trace.channels.x),
    y: unpackFloats(trace.channels.y),
    offset: unpackFloats(trace.channels.offset),
    sectors: unpackFloats(trace.sectors),
  };
}

// ---------------------------------------------------------------------------
// Query shapes
// ---------------------------------------------------------------------------

export interface PageQuery {
  page?: number;
  pageSize?: number;
}

export interface SessionListQuery extends PageQuery {
  trackId?: string;
  visibility?: SessionVisibility;
  /** Free text over track name, vehicle, driver and file name. */
  q?: string;
  from?: string;
  to?: string;
  sort?: 'recordedAt' | 'uploadedAt' | 'bestLapMs';
  dir?: 'asc' | 'desc';
}

export interface AdminSessionListQuery extends SessionListQuery {
  userId?: string;
}

export interface AdminUserListQuery extends PageQuery {
  q?: string;
  role?: UserRole;
  status?: UserStatus;
  sort?: 'createdAt' | 'lastLoginAt' | 'nickname' | 'sessionCount';
  dir?: 'asc' | 'desc';
}

export interface LoginListQuery extends PageQuery {
  userId?: string;
  email?: string;
  success?: boolean;
  from?: string;
  to?: string;
  q?: string;
}

export interface LeaderboardQuery extends PageQuery {
  /** Restrict to a vehicle class when the track hosts several. */
  vehicle?: string;
  /** Include the per-microsector network bests. Costs a heavier query. */
  withSectors?: boolean;
}

export interface UpdateSessionRequest {
  visibility?: SessionVisibility;
  notes?: string;
  vehicle?: string;
  driver?: string;
}

export interface UpdateProfileRequest extends UpdateUserRequest {
  /** Present only when the user is changing their password. */
  currentPassword?: string;
  newPassword?: string;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const api = {
  auth: {
    login: (body: LoginRequest, signal?: AbortSignal) =>
      request<AuthResponse>('/auth/login', { method: 'POST', body, anonymous: true, signal }),

    register: (body: RegisterRequest, signal?: AbortSignal) =>
      request<AuthResponse>('/auth/register', { method: 'POST', body, anonymous: true, signal }),

    refresh: (refreshToken: string, signal?: AbortSignal) =>
      request<AuthTokens>('/auth/refresh', {
        method: 'POST',
        body: { refreshToken },
        anonymous: true,
        noRetry: true,
        signal,
      }),

    logout: (refreshToken: string | null, signal?: AbortSignal) =>
      request<void>('/auth/logout', { method: 'POST', body: { refreshToken }, noRetry: true, signal }),

    me: (signal?: AbortSignal) => request<User>('/me', { signal }),

    /** Which sign-in methods the server actually has configured. */
    providers: (signal?: AbortSignal) =>
      request<AuthProviders>('/auth/providers', { anonymous: true, signal }),

    /**
     * Redeems the single-use code the Google callback leaves in the URL.
     *
     * `noRetry` because there is nothing to retry: the code is burned on the
     * first attempt, so a second one would fail with a confusing "expirou".
     */
    googleExchange: (code: string, signal?: AbortSignal) =>
      request<GoogleAuthResponse>('/auth/google/exchange', {
        method: 'POST',
        body: { code },
        anonymous: true,
        noRetry: true,
        signal,
      }),

    changePassword: (
      body: { currentPassword: string; newPassword: string },
      signal?: AbortSignal,
    ) => request<{ changed: boolean; revokedSessions: number }>('/auth/change-password', {
      method: 'POST',
      body,
      signal,
    }),
  },

  profile: {
    get: (signal?: AbortSignal) => request<User>('/me', { signal }),

    update: (body: UpdateProfileRequest, signal?: AbortSignal) =>
      request<User>('/me', { method: 'PATCH', body, signal }),

    stats: (signal?: AbortSignal) => request<UserStats>('/me/stats', { signal }),

    /** Live refresh sessions — every device currently signed in as this user. */
    sessions: (signal?: AbortSignal) =>
      request<{ items: ActiveAuthSession[] }>('/me/sessions', { signal }).then((r) => r.items),

    revokeSession: (sessionId: string, signal?: AbortSignal) =>
      request<{ revoked: boolean }>(`/me/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
        signal,
      }),
  },

  tracks: {
    // The endpoint answers with a paginated envelope; callers only ever want the
    // tracks themselves, so it is unwrapped here rather than at every call site.
    list: (signal?: AbortSignal) =>
      request<{ items: TrackDto[]; total: number }>('/tracks', { signal }).then((r) => r.items),

    get: (trackId: string, signal?: AbortSignal) =>
      request<TrackDto>(`/tracks/${encodeURIComponent(trackId)}`, { signal }),

    /** Asks the server whether a track just parsed locally is one it already knows. */
    match: (
      body: { name: string; lat0: number; lon0: number; lengthM: number },
      signal?: AbortSignal,
    ) =>
      request<{ track: TrackDto | null; matched: boolean; distanceM: number }>('/tracks/match', {
        method: 'POST',
        body,
        signal,
      }),
  },

  sessions: {
    list: (query: SessionListQuery = {}, signal?: AbortSignal) =>
      request<Paginated<SessionSummaryDto>>('/sessions', { query: toQuery(query), signal }),

    get: (sessionId: string, signal?: AbortSignal) =>
      request<SessionDetailDto>(`/sessions/${encodeURIComponent(sessionId)}`, { signal }),

    create: (body: CreateSessionRequest, signal?: AbortSignal) =>
      request<SessionSummaryDto>('/sessions', { method: 'POST', body, signal }),

    update: (sessionId: string, body: UpdateSessionRequest, signal?: AbortSignal) =>
      request<SessionSummaryDto>(`/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'PATCH',
        body,
        signal,
      }),

    remove: (sessionId: string, signal?: AbortSignal) =>
      request<void>(`/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE', signal }),

    /** Full session export as a JSON blob, for backup or sharing. */
    export: (sessionId: string, signal?: AbortSignal) =>
      requestBlob(`/sessions/${encodeURIComponent(sessionId)}/export`, { signal }),
  },

  laps: {
    /**
     * One request serves both the summary and the trace, so `get` and `trace`
     * are two views of the same call rather than two round trips.
     */
    full: (lapId: string, signal?: AbortSignal) =>
      request<{ lap: LapSummaryDto; trace: LapTraceDto; sessionId: string; trackId: string }>(
        `/laps/${encodeURIComponent(lapId)}`,
        { signal },
      ),

    get: (lapId: string, signal?: AbortSignal) =>
      request<{ lap: LapSummaryDto }>(`/laps/${encodeURIComponent(lapId)}`, { signal }).then(
        (r) => r.lap,
      ),

    trace: (lapId: string, signal?: AbortSignal) =>
      request<{ trace: LapTraceDto }>(`/laps/${encodeURIComponent(lapId)}`, { signal }).then(
        (r) => r.trace,
      ),

    compare: (body: CompareRequest, signal?: AbortSignal) =>
      request<CompareResponse>('/laps/compare', { method: 'POST', body, signal }),

    /** A user's quickest laps, optionally restricted to one track. */
    best: (query: { userId?: string; trackId?: string; limit?: number } = {}, signal?: AbortSignal) =>
      request<{ items: LapSummaryDto[] }>('/laps/best', { query: toQuery(query), signal }).then(
        (r) => r.items,
      ),

    /** This lap measured against the whole network, microsector by microsector. */
    networkComparison: (lapId: string, signal?: AbortSignal) =>
      request<NetworkComparison>(`/network/compare/${encodeURIComponent(lapId)}`, { signal }),
  },

  leaderboard: {
    get: (trackId: string, query: LeaderboardQuery = {}, signal?: AbortSignal) =>
      request<LeaderboardResponse>(`/network/leaderboard/${encodeURIComponent(trackId)}`, {
        query: toQuery(query),
        signal,
      }),
  },

  devices: {
    discover: (signal?: AbortSignal) => request<DiscoveredDevice[]>('/devices/discover', { signal }),

    listFiles: (deviceId: string, signal?: AbortSignal) =>
      request<DeviceListFilesResponse>(`/devices/${encodeURIComponent(deviceId)}/files`, { signal }),

    download: (deviceId: string, path: string, signal?: AbortSignal) =>
      requestBlob(`/devices/${encodeURIComponent(deviceId)}/download`, { query: { path }, signal }),
  },

  admin: {
    stats: (signal?: AbortSignal) => request<AdminStats>('/admin/stats', { signal }),

    users: {
      list: (query: AdminUserListQuery = {}, signal?: AbortSignal) =>
        request<Paginated<AdminUserView>>('/admin/users', { query: toQuery(query), signal }),

      get: (userId: string, signal?: AbortSignal) =>
        request<AdminUserView>(`/admin/users/${encodeURIComponent(userId)}`, { signal }),

      update: (userId: string, body: UpdateUserRequest, signal?: AbortSignal) =>
        request<AdminUserView>(`/admin/users/${encodeURIComponent(userId)}`, {
          method: 'PATCH',
          body,
          signal,
        }),

      suspend: (userId: string, reason?: string, signal?: AbortSignal) =>
        request<AdminUserView>(`/admin/users/${encodeURIComponent(userId)}/suspend`, {
          method: 'POST',
          body: { reason },
          signal,
        }),

      reinstate: (userId: string, signal?: AbortSignal) =>
        request<AdminUserView>(`/admin/users/${encodeURIComponent(userId)}/reinstate`, {
          method: 'POST',
          signal,
        }),

      remove: (userId: string, signal?: AbortSignal) =>
        request<void>(`/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE', signal }),

      /** Revokes one live refresh session (kicks a device out). */
      revokeSession: (userId: string, sessionId: string, signal?: AbortSignal) =>
        request<AdminUserView>(
          `/admin/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(sessionId)}`,
          { method: 'DELETE', signal },
        ),
    },

    sessions: {
      list: (query: AdminSessionListQuery = {}, signal?: AbortSignal) =>
        request<Paginated<SessionSummaryDto>>('/admin/sessions', { query: toQuery(query), signal }),

      remove: (sessionId: string, signal?: AbortSignal) =>
        request<void>(`/admin/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE', signal }),
    },

    audit: {
      list: (query: AuditQuery = {}, signal?: AbortSignal) =>
        request<Paginated<AuditEvent>>('/admin/audit', { query: toQuery(query), signal }),

      export: (query: AuditQuery = {}, signal?: AbortSignal) =>
        requestBlob('/admin/audit.csv', { query: toQuery(query), signal }),
    },

    logins: {
      list: (query: LoginListQuery = {}, signal?: AbortSignal) =>
        request<Paginated<LoginEvent>>('/admin/logins', { query: toQuery(query), signal }),
    },
  },
};

export type Api = typeof api;
export type { NetworkComparisonResponse };
